/**
 * journey.viewMode — shared Simple ↔ Detailed toggle for the employee journey
 * (Impact Portfolio, Objectives overview, etc).
 *
 * Persisted in localStorage so the user's preference is consistent across
 * surfaces and reloads. The default is 'simple' — the "safe space" view that
 * hides RAG colors, At-Risk pills, classification, validation badges, and
 * progress percentages. 'detailed' restores the full set of metrics for users
 * (managers / HR / power users) who want them.
 *
 * Sync model:
 *  - SAME document: a custom `journey-view-mode-change` event is dispatched on
 *    every update, and every hook instance listens for it — so toggling on one
 *    surface (e.g. the nav switch) instantly re-renders every other component
 *    that reads the mode (KPA cards, one-pager, portfolio, etc.), even when
 *    they live in unrelated parts of the tree. (The native `storage` event does
 *    NOT fire in the document that made the change, so it can't do this.)
 *  - OTHER tabs: the native `storage` event keeps additional open tabs in sync.
 */

import { useCallback, useEffect, useState } from 'react'

export type JourneyViewMode = 'simple' | 'detailed'

const STORAGE_KEY = 'journey.viewMode'
const CHANGE_EVENT = 'journey-view-mode-change'
const DEFAULT_MODE: JourneyViewMode = 'simple'

const isViewMode = (value: string | null): value is JourneyViewMode =>
  value === 'simple' || value === 'detailed'

const readStoredMode = (): JourneyViewMode => {
  if (typeof window === 'undefined') return DEFAULT_MODE
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isViewMode(stored) ? stored : DEFAULT_MODE
  } catch {
    return DEFAULT_MODE
  }
}

export function useJourneyViewMode(): [JourneyViewMode, (next: JourneyViewMode) => void] {
  const [mode, setMode] = useState<JourneyViewMode>(readStoredMode)

  useEffect(() => {
    // Same-document sync: every instance reacts to the custom change event.
    const onLocalChange = (event: Event) => {
      const next = (event as CustomEvent<JourneyViewMode>).detail
      if (isViewMode(next)) setMode(next)
    }
    // Cross-tab sync: native storage event (fires only in OTHER tabs).
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && isViewMode(event.newValue)) {
        setMode(event.newValue)
      }
    }
    window.addEventListener(CHANGE_EVENT, onLocalChange as EventListener)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CHANGE_EVENT, onLocalChange as EventListener)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const update = useCallback((next: JourneyViewMode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // localStorage unavailable (private mode, etc.) — fail silently; the
      // dispatched event below still updates in-memory state for the session.
    }
    // Notify every hook instance in THIS document (incl. the caller) so they
    // all re-render with the new mode.
    window.dispatchEvent(new CustomEvent<JourneyViewMode>(CHANGE_EVENT, { detail: next }))
  }, [])

  return [mode, update]
}
